// Recharts components
import {
  PieChart,
  Pie,
  Tooltip,
  Legend,
  Cell,
  ResponsiveContainer,
  Label,
} from 'recharts';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsRTL } from '../../redux/selectors/displaySettingsSelectors';

// i18n hooks
import { useTranslation } from 'react-i18next';

// Custom hooks
import { useChartConfig } from './hooks/useChartConfig';
import { usePieActiveCell } from './hooks/usePieActiveCell';

// Shared components
import { ChartContainer } from './shared/ChartContainer';
import { ChartContent } from './shared/ChartContent';
import { ChartFooter } from './shared/ChartFooter';

interface GenderData {
  type: string;
  count: number;
  percentage: number;
}

interface GenderPieChartProps {
  data: GenderData[];
}

export const GenderPieChart = ({ data }: GenderPieChartProps) => {
  const { t } = useTranslation();
  const isRTL = useAppSelector(selectIsRTL);
  const total = data.reduce((acc, curr) => acc + curr.count, 0);

  const config = useChartConfig();

  const { getCellProps } = usePieActiveCell();

  // Transform data for chart
  const chartData = data.map((item) => ({
    name: t(`demographics.gender.${item.type.toLowerCase()}`),
    value: item.count,
    percentage: item.percentage,
  }));

  const COLORS = [
    config.colors.primary,
    config.colors.secondary,
    config.colors.status.success,
    config.colors.status.errorMuted,
  ];

  return (
    <ChartContainer>
      {/* Header */}
      <div className="p-4 text-center">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('demographics.genderDistribution')}
        </h2>
        <p className="text-sm text-gray-700">
          {t('demographics.genderDistributionDescription')}
        </p>
      </div>

      {/* Chart */}
      <ChartContent>
        <style>
          {`
            .recharts-legend-item .recharts-surface {
              margin-${isRTL ? 'left' : 'right'}: 8px !important;
            }
          `}
        </style>
        <ResponsiveContainer
          width="100%"
          height={360}
          style={config.styles.noOutline}
        >
          <PieChart onClick={undefined} style={config.styles.noOutline}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              dataKey="value"
              onClick={undefined}
              style={config.styles.noOutline}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  {...getCellProps(index, {
                    suppressActive: data.length === 1 || entry.value === total,
                  })}
                />
              ))}
              {/* Center label */}
              <Label
                content={({ viewBox }) => {
                  if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="central"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) - 25}
                          className="fill-gray-900 text-2xl font-bold"
                        >
                          {total}
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 5}
                          className="fill-gray-700 text-sm"
                        >
                          {t('dashboard.dataLabels.participants')}
                        </tspan>
                      </text>
                    );
                  }
                }}
              />
            </Pie>
            <Tooltip {...config.tooltip} />
            <Legend
              {...config.legend}
              iconType="rect"
              layout="horizontal"
              align="center"
              verticalAlign="bottom"
            />
          </PieChart>
        </ResponsiveContainer>
      </ChartContent>

      {/* Footer */}
      <ChartFooter>{t('demographics.genderDistributionShowing')}</ChartFooter>
    </ChartContainer>
  );
};
